import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { AdvocacyAddressWrongStateError } from 'utils/errors/client-errors';
import { useSessionContext } from 'contexts/session';
import { AdvocacyRepresentatives } from 'contexts/advocacy/advocacy.typings';

interface ValidateStateParam {
  representatives?: AdvocacyRepresentatives;
  stateAbbreviation?: string;
  stateFullName?: string;
}

export const validateState = ({
  representatives,
  stateAbbreviation,
  stateFullName,
}: ValidateStateParam) => {
  let wrongStateError;
  if (
    representatives &&
    stateAbbreviation !== representatives[0].stateAbbreviation
  ) {
    wrongStateError = new AdvocacyAddressWrongStateError(stateFullName);
  }
  return wrongStateError;
};

interface Params {
  stateAbbreviation?: string;
  stateFullName?: string;
  onCompleted?: () => void;
  onError?: () => void;
}

export const useGetAdvocacyRepresentatives = ({
  stateAbbreviation,
  stateFullName,
  onCompleted,
  onError,
}: Params) => {
  const { userId } = useSessionContext();
  const [getData, { data, loading, error }] = useLazyQuery<
    GetAdvocacyRepresentativesQuery,
    GetAdvocacyRepresentativesQueryVariables
  >(GET_ADVOCACY_REPRESENTATIVES, { onCompleted, onError });
  const triggerGetData = useCallback(
    ({ address }) => {
      getData({
        variables: {
          address,
          id: userId,
        },
      });
    },
    [userId, getData]
  );

  const representatives = data?.user?.advocacyRepresentatives;

  const wrongStateError = validateState({
    representatives,
    stateAbbreviation,
    stateFullName,
  });

  return [
    triggerGetData,
    {
      data: { representatives },
      error: error || wrongStateError,
      loading,
    },
  ] as const;
};

export const GET_ADVOCACY_REPRESENTATIVES = gql`
  query getAdvocacyRepresentatives($id: Int!, $address: String!) {
    user(id: $id) {
      id
      name
      advocacyRepresentatives(address: $address) {
        name
        phone
        website
        stateAbbreviation
        title
        email
        level
      }
    }
  }
`;

export const getAdvocacyRepresentativesMock = {
  user: {
    advocacyRepresentatives: [
      {
        email: 'sundance@kid.com',
        level: 'administrativeArea1',
        name: 'Sundance Kid',
        phone: '(555) 555-5555',
        stateAbbreviation: 'FK',
        title: 'PA State Senator',
        website: 'https://www.sundance.org',
      },
    ],
    id: 10,
    name: 'Rachel Carson',
  },
};

export const useGetAdvocacyRepresentativesMock = {
  data: {
    representatives:
      getAdvocacyRepresentativesMock.user.advocacyRepresentatives,
  },
  error: undefined,
  loading: false,
};
